import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { NotificationIcon } from "components/icons";
import MentionsBellTip from "components/mentions/launch/mentions-bell-tip.component";
import MaybeMentionsNewTabLabel from "components/mentions/launch/mentions-new-tab-label.component";
import {
  INBOX_V2_FEATURE,
  TEAM_MENTIONS_FEATURE
} from "constants/feature-flags";
import useTextRoutingAlerts from "features/browser-notifications/use-text-routing-alerts.hook";
import { useRefreshTaskCountOnTaskNotification } from "features/patients/patient-header/header-tasks/use-task-count-freshness.hook";
import useSalesCoachRouteAccess from "features/sales-coach/hooks/use-sales-coach-route-access.hook";
import useIsMobile from "hooks/use-is-mobile.hook";
import useNotifications from "hooks/use-notifications";
import { NotificationTypes } from "services/types/notifications";

import NotificationItemV2 from "./notification-item/notification-item-v2.component";
import NotificationTab from "./notification-tab/notification-tab.component";
import { isSalesReviewNotification } from "./sales-review-notification.util";
import useHiddenSalesReviewCount from "./use-hidden-sales-review-count.hook";

export default function NotificationsMenu() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);

  const flags = useFlags();
  const isInboxV2Enabled = flags[INBOX_V2_FEATURE];
  const isTeamMentionsEnabled = Boolean(flags[TEAM_MENTIONS_FEATURE]);

  const {
    notifications,
    deleteNotification,
    activeType,
    readNotification,
    setActiveType,
    unreadNotificationsCount,
    handleReadAllNotifications,
    isReadingAll,
    refetch,
    loadMore,
    hasMore,
    isLoadingMore,
    lastJsonMessage
  } = useNotifications({ syncReadsFromOtherTabs: true });

  // Patient text routing: desktop alert + in-app toast for a newly routed text.
  // Mounted here, once, because this bell is always on screen (see the hook).
  useTextRoutingAlerts(lastJsonMessage);
  // Task badge in the top bar: refresh it when a task notification arrives.
  // Mounted here for the same reason as the line above.
  useRefreshTaskCountOnTaskNotification(lastJsonMessage);

  const onOpen = useCallback(() => {
    refetch();
    setIsOpen(true);
  }, [refetch]);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Close once the server confirms; on failure the panel stays open with the rows
  // still unread, so it never looks like the read worked when it did not.
  const onReadAll = useCallback(async () => {
    if (await handleReadAllNotifications()) {
      setIsOpen(false);
    }
  }, [handleReadAllNotifications]);

  // Sales Coach reviews are the only reason this list is filtered (SN-19629). The
  // backend writes their bell row for every review that scores, with no Sales Coach
  // gating of its own — the review pipeline runs fleet-wide and `salesCoachDashboard`
  // is a client-side flag — so practices with the section switched off were being told
  // about reviews they have no way to open.
  //
  // Read through the route-access hook, never `flags[SALES_COACH_DASHBOARD_FEATURE]`
  // inline: `features/sales-coach/CLAUDE.md` makes that hook the single owner of this
  // gate, and it already handles the flag-delivery race that shipped as a bug once.
  // `isResolving` is deliberately ignored — `isEnabled` is `=== true`, so it reads
  // false until the flags land, which hides the rows a beat too long rather than
  // showing them and snatching them back.
  const { isEnabled: isSalesCoachEnabled, isResolving: isSalesCoachResolving } =
    useSalesCoachRouteAccess();

  const filteredNotifications = useMemo(
    () =>
      isSalesCoachEnabled
        ? notifications
        : notifications.filter(
            (notification) => !isSalesReviewNotification(notification)
          ),
    [isSalesCoachEnabled, notifications]
  );

  // The unread counts come from a server aggregate grouped by `type`, and sales
  // reviews share type 8 with the scribe notifications — so the server cannot exclude
  // the rows we just hid and the badge counted them (SN-19629). Correct it here.
  //
  // `isResolving` is honoured for this, and deliberately NOT for the filter above: an
  // unresolved flag should hide rows (free, and the safe direction) but must not fire
  // a request, or every Sales-Coach-enabled practice pays for a scan it never uses.
  // `all > 0` skips the request when there is nothing to correct, and sequences it
  // after the counts query so the deliberate 2s notifications delay is respected.
  const hiddenSalesReviewCount = useHiddenSalesReviewCount({
    enabled:
      !isSalesCoachResolving &&
      !isSalesCoachEnabled &&
      unreadNotificationsCount.all > 0,
    lastJsonMessage
  });

  // Floors at 0 rather than padding: the two numbers come from separate requests, so
  // a row read between them would otherwise render a negative badge. NOTE the type-8
  // bucket inside `unreadNotificationsCount` is itself still uncorrected — nothing
  // renders it today, but a future type-8 tab would inherit the bug.
  const unreadCount = Math.max(
    unreadNotificationsCount.all - hiddenSalesReviewCount,
    0
  );

  return (
    <>
      <Box component="span" mr={isMobile ? -1 : 0}>
        <IconButton onClick={onOpen} data-testid="notifications-button">
          <Badge
            color="primary"
            data-testid="unread-notifications"
            badgeContent={unreadCount}
            sx={{ zIndex: 0 }}
          >
            <NotificationIcon />
          </Badge>
        </IconButton>
      </Box>

      <Drawer
        PaperProps={{
          sx: {
            width: 630
          }
        }}
        variant="persistent"
        anchor="right"
        sx={{
          zIndex: theme.zIndex.snackbar + 1
        }}
        open={isOpen}
        onClose={onClose}
      >
        {isOpen && (
          <>
            <Box
              display="flex"
              justifyContent="end"
              position="absolute"
              width="100%"
            >
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box padding="32px 16px 16px">
              <Stack
                justifyContent="center"
                sx={{ width: "100%", height: "100%" }}
                spacing={1}
              >
                <Tabs
                  centered
                  variant="fullWidth"
                  value={activeType}
                  onChange={(_, value) => setActiveType(value)}
                  sx={{
                    overflow: "visible",
                    "& > div": { overflow: "visible !important" }
                  }}
                >
                  <Tab
                    value={null}
                    label={
                      <NotificationTab count={unreadCount}>All</NotificationTab>
                    }
                    sx={tabSx}
                  />

                  {isTeamMentionsEnabled && (
                    <Tab
                      value={NotificationTypes.MENTION}
                      label={
                        <MaybeMentionsNewTabLabel
                          tab="bellTab"
                          isActive={activeType === NotificationTypes.MENTION}
                        >
                          <NotificationTab
                            count={
                              unreadNotificationsCount[
                                NotificationTypes.MENTION
                              ]
                            }
                          >
                            Mentions
                          </NotificationTab>
                        </MaybeMentionsNewTabLabel>
                      }
                      sx={tabSx}
                    />
                  )}

                  {isInboxV2Enabled && (
                    <Tab
                      value={NotificationTypes.MESSAGE}
                      label={
                        <NotificationTab
                          count={
                            unreadNotificationsCount[NotificationTypes.MESSAGE]
                          }
                        >
                          Assigned Messages
                        </NotificationTab>
                      }
                      sx={tabSx}
                    />
                  )}

                  <Tab
                    value={NotificationTypes.REPORT}
                    label={
                      <NotificationTab
                        count={
                          unreadNotificationsCount[NotificationTypes.REPORT]
                        }
                      >
                        Reports
                      </NotificationTab>
                    }
                    sx={tabSx}
                  />

                  <Tab
                    value={NotificationTypes.INTAKE_FORM}
                    label={
                      <NotificationTab
                        count={
                          unreadNotificationsCount[
                            NotificationTypes.INTAKE_FORM
                          ]
                        }
                      >
                        Onboarding Forms
                      </NotificationTab>
                    }
                    sx={tabSx}
                  />
                </Tabs>
              </Stack>
            </Box>
            <List
              sx={{
                display: "flex",
                flexDirection: "column",
                px: 2,
                gap: 1,
                overflowY: "auto"
              }}
            >
              {isTeamMentionsEnabled &&
                activeType === NotificationTypes.MENTION && (
                  <MentionsBellTip onNavigate={onClose} />
                )}

              {/* `!hasMore` matters twice: a page whose rows are all filtered out
                  would otherwise sit under a "Load more" button reading "Empty
                  notifications", and `hasMore` starts `true` before any response
                  arrives, so this also stops the copy flashing during the first
                  load. */}
              {filteredNotifications.length === 0 && !hasMore && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    boxSizing: "border-box",
                    padding: 2
                  }}
                >
                  <Typography color="GrayText">Empty notifications</Typography>
                </Box>
              )}

              {filteredNotifications.map((notification) => {
                return (
                  <NotificationItemV2
                    key={notification.id}
                    notification={notification}
                    onDelete={deleteNotification}
                    readNotification={readNotification}
                  />
                );
              })}

              {hasMore && (
                <Box display="flex" justifyContent="center" py={2}>
                  <Button
                    size="small"
                    onClick={loadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? "Loading..." : "Load more"}
                  </Button>
                </Box>
              )}
            </List>

            {notifications.length > 0 && (
              <Button
                size="small"
                onClick={onReadAll}
                disabled={isReadingAll}
                sx={{ marginY: 1 }}
              >
                Read all notifications
              </Button>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}

const tabSx = {
  fontSize: "14px",
  padding: 0,
  overflow: "visible"
};
